<script setup>
import { computed } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import BrandHeader from '@/Components/Public/BrandHeader.vue'
import BrandFooter from '@/Components/Public/BrandFooter.vue'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import ThemeMenu from '@/Components/Public/ThemeMenu.vue'

const page = usePage()
const flash = computed(() => page.props.flash || {})
const currentLocale = computed(() => page.props.locale || 'en')
const { t } = useI18n()

function setLang(lang) {
  if (lang === currentLocale.value) return
  router.get(window.location.pathname, { lang }, { preserveScroll: true, preserveState: false })
}
function toLogins() {
  document.getElementById('partner-logins')?.scrollIntoView({ behavior: 'smooth' })
}

const localeOptions = [
  { value: 'en', label: 'English', sub: 'EN' },
  { value: 'ru', label: 'Русский', sub: 'RU' },
  { value: 'zh', label: '中文',    sub: 'ZH' },
  { value: 'th', label: 'ไทย',     sub: 'TH' }
]
</script>

<template>
  <div class="mkt">
    <BrandHeader>
      <template #tools>
        <div class="mkt__nav">
          <button
            type="button"
            class="mkt__login"
            @click="toLogins"
          >{{ t('partners.index.header_login') }}</button>
          <HeaderMenu :aria-label="t('public.menu.language')">
            <template #icon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M3 12h18"/>
                <path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>
              </svg>
            </template>
            <template #default="{ close }">
              <button v-for="opt in localeOptions" :key="opt.value" type="button" class="hmenu__item" @click="setLang(opt.value); close()">
                <span>{{ opt.label }} <span class="hmenu__item-sub">({{ opt.sub }})</span></span>
                <svg v-if="opt.value === currentLocale" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>
          <ThemeMenu />
        </div>
      </template>
    </BrandHeader>

    <div
      v-if="flash.alert"
      class="mkt__flash mkt__flash--alert"
      role="alert"
    >{{ flash.alert }}</div>
    <div
      v-if="flash.notice"
      class="mkt__flash mkt__flash--notice"
    >{{ flash.notice }}</div>

    <main class="mkt__body"><slot /></main>

    <BrandFooter>
      <span>{{ t('partners.index.footer_copy') }}</span>
    </BrandFooter>
  </div>
</template>

<style scoped>
.mkt { min-height: 100vh; display: flex; flex-direction: column; background: var(--bg); }
.mkt__nav { display: flex; align-items: center; gap: 1.25rem; }
.mkt__login { background: transparent; border: 0; color: var(--accent); font-weight: 600; font-size: 0.9375rem; cursor: pointer; padding: 0; }
.mkt__flash { max-width: 64rem; margin: 0.75rem auto 0; width: calc(100% - 2rem); display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; border-radius: var(--radius-input, 0.5rem); font-size: 0.9375rem; }
.mkt__flash--alert { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); }
.mkt__flash--notice { background: color-mix(in oklch, var(--ok) 12%, var(--surface)); color: var(--ok); }
.mkt__body { flex: 1; }
.mkt__nav :deep(.hmenu__item) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  background: transparent;
  border: 0;
  border-radius: 0.4rem;
  color: var(--ink);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.mkt__nav :deep(.hmenu__item:hover) { background: color-mix(in oklch, var(--ink) 6%, transparent); }
.mkt__nav :deep(.hmenu__item-sub) { color: var(--muted); font-size: 0.8125rem; margin-left: 0.125rem; }
.mkt__nav :deep(.hmenu__check) { width: 1rem; height: 1rem; color: var(--accent); flex-shrink: 0; }
</style>
